import React, { useEffect, useState, useCallback } from 'react';

const API = process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:5295';

const STATUS_STYLES = {
  Active:        { bg: '#E8F5E9', color: '#2E7D32' },
  Pending:       { bg: '#FFF8E1', color: '#F57F17' },
  PendingAdmin:  { bg: '#FFF8E1', color: '#F57F17' },
  Suspended:     { bg: '#FFEBEE', color: '#C62828' },
  Cancelled:     { bg: '#ECEFF1', color: '#455A64' },
};

const STATUS_VALUES = ['Active', 'Pending', 'PendingAdmin', 'Suspended', 'Cancelled'];

export default function AdminTenantsPage() {
  const [tenants, setTenants] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // { tenantId } — drawer state
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = statusFilter ? `?status=${statusFilter}` : '';
      const res = await fetch(`${API}/api/v1/admin/tenants${qs}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setTenants(await res.json());
    } catch (e) { setError(e.message); }
    setLoading(false);
  }, [statusFilter]);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div style={{ maxWidth: 1100, margin: '32px auto', padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: '#0D1B2A' }}>Tenants</h2>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            style={{ padding: '6px 10px', fontSize: 12, border: '1px solid #E0E4E8', borderRadius: 6 }}>
            <option value="">All statuses</option>
            {STATUS_VALUES.map(s => <option key={s}>{s}</option>)}
          </select>
          <button className="btn-secondary" onClick={refresh}>↻ Refresh</button>
        </div>
      </div>
      <div style={{ fontSize: 11, color: '#7A8899', marginBottom: 20 }}>
        Every Tenant row across the platform. Marketplace-provisioned tenants carry a Subscription;
        manual tenants (e.g. Kloudynet's own) don't.
      </div>

      {error && (
        <div style={{ padding: 10, marginBottom: 16, background: '#FFEBEE', color: '#C62828', borderRadius: 6, fontSize: 12 }}>
          {error}
        </div>
      )}

      <div style={{ background: '#fff', border: '1px solid #E8ECF0', borderRadius: 10, overflow: 'hidden' }}>
        <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#F8F9FA' }}>
              <th style={th}>Tenant id</th>
              <th style={th}>Display name</th>
              <th style={th}>Status</th>
              <th style={th}>Source</th>
              <th style={th}>Activated</th>
              <th style={{ ...th, textAlign: 'right' }}></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={{ padding: 20, textAlign: 'center', color: '#7A8899' }}>Loading…</td></tr>
            ) : tenants.length === 0 ? (
              <tr><td colSpan={6} style={{ padding: 20, textAlign: 'center', color: '#7A8899' }}>No tenants match.</td></tr>
            ) : tenants.map(t => {
              const stStyle = STATUS_STYLES[t.status] || { bg: '#F5F7F9', color: '#455A64' };
              return (
                <tr key={t.id} style={{ borderTop: '1px solid #F0F2F5' }}>
                  <td style={{ ...td, fontFamily: 'ui-monospace,Menlo,monospace', fontSize: 10 }}>{t.id}</td>
                  <td style={td}>{t.displayName}</td>
                  <td style={td}>
                    <span style={{
                      fontSize: 10, fontWeight: 600, padding: '3px 8px', borderRadius: 10,
                      background: stStyle.bg, color: stStyle.color,
                    }}>{t.status}</span>
                  </td>
                  <td style={td}>{t.isManual ? <span style={{ color: '#7A8899' }}>manual</span> : 'marketplace'}</td>
                  <td style={td}>{t.activatedAt ? new Date(t.activatedAt).toLocaleDateString() : '—'}</td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    <button className="btn-secondary" style={{ fontSize: 10 }} onClick={() => setSelected({ tenantId: t.id })}>
                      View
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {selected && <TenantDetailDrawer tenantId={selected.tenantId} onClose={() => setSelected(null)} onAction={refresh} />}
    </div>
  );
}

function TenantDetailDrawer({ tenantId, onClose, onAction }) {
  const [tenant, setTenant] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [webhooks, setWebhooks] = useState([]);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const [showRebind, setShowRebind] = useState(false);
  const [rebindEmail, setRebindEmail] = useState('');
  const [showMarkManual, setShowMarkManual] = useState(false);
  const [manualDisplayName, setManualDisplayName] = useState('');

  const load = useCallback(async () => {
    try {
      const [t, s, w] = await Promise.all([
        fetch(`${API}/api/v1/admin/tenants/${tenantId}`).then(r => r.ok ? r.json() : null),
        fetch(`${API}/api/v1/admin/tenants/${tenantId}/subscription`).then(r => r.ok ? r.json() : null),
        fetch(`${API}/api/v1/admin/tenants/${tenantId}/webhooks?limit=50`).then(r => r.ok ? r.json() : []),
      ]);
      setTenant(t);
      setSubscription(s);
      setWebhooks(w || []);
      if (t?.displayName) setManualDisplayName(t.displayName);
    } catch (e) { setToast({ kind: 'err', text: e.message }); }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const showToast = (kind, text) => {
    setToast({ kind, text });
    setTimeout(() => setToast(null), 4000);
  };

  const resync = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/v1/admin/tenants/${tenantId}/resync`, { method: 'POST' });
      if (!res.ok) throw new Error(await res.text() || `HTTP ${res.status}`);
      showToast('ok', 'Resynced from Marketplace.');
      await load();
      onAction();
    } catch (e) { showToast('err', e.message); }
    setBusy(false);
  };

  const replay = async (webhookId) => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/v1/admin/tenants/webhooks/${webhookId}/replay`, { method: 'POST' });
      if (!res.ok) throw new Error(await res.text() || `HTTP ${res.status}`);
      showToast('ok', 'Webhook replayed.');
      await load();
      onAction();
    } catch (e) { showToast('err', e.message); }
    setBusy(false);
  };

  const markManual = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/v1/admin/tenants/${tenantId}/mark-manual`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ displayName: manualDisplayName }),
      });
      if (!res.ok) throw new Error(await res.text() || `HTTP ${res.status}`);
      showToast('ok', 'Tenant marked as manual.');
      setShowMarkManual(false);
      await load();
      onAction();
    } catch (e) { showToast('err', e.message); }
    setBusy(false);
  };

  const rebind = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/v1/admin/tenants/${tenantId}/rebind-purchaser`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ newPurchaserEmail: rebindEmail }),
      });
      if (!res.ok) throw new Error(await res.text() || `HTTP ${res.status}`);
      showToast('ok', 'Purchaser rebound.');
      setShowRebind(false);
      setRebindEmail('');
      await load();
    } catch (e) { showToast('err', e.message); }
    setBusy(false);
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(13,27,42,0.45)', zIndex: 100,
      display: 'flex', justifyContent: 'flex-end',
    }} onClick={onClose}>
      <div style={{
        width: 560, height: '100vh', background: '#fff', overflowY: 'auto',
        boxShadow: '-12px 0 40px rgba(0,0,0,0.2)',
      }} onClick={e => e.stopPropagation()}>
        <div style={{
          padding: '14px 20px', borderBottom: '1px solid #E8ECF0',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, background: '#fff', zIndex: 1,
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#0D1B2A' }}>Tenant detail</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#7A8899' }}>×</button>
        </div>

        {toast && (
          <div style={{
            margin: '10px 20px', padding: 10, borderRadius: 6, fontSize: 11,
            background: toast.kind === 'ok' ? '#E8F5E9' : '#FFEBEE',
            color: toast.kind === 'ok' ? '#2E7D32' : '#C62828',
          }}>{toast.text}</div>
        )}

        <div style={{ padding: 20 }}>
          {!tenant ? <div style={{ fontSize: 11, color: '#7A8899' }}>Loading…</div> : (
            <>
              <Section title="Tenant">
                <Row label="Id" value={<code style={{ fontSize: 10 }}>{tenant.id}</code>} />
                <Row label="Display name" value={tenant.displayName} />
                <Row label="Status" value={tenant.status} />
                <Row label="Source" value={tenant.isManual ? 'manual' : 'marketplace'} />
                <Row label="Entra tenant" value={tenant.entraTenantId || '—'} />
                <Row label="Data residency" value={tenant.dataResidencyRegion || '—'} />
                <Row label="Activated" value={tenant.activatedAt ? new Date(tenant.activatedAt).toLocaleString() : '—'} />
                <Row label="Suspended" value={tenant.suspendedAt ? new Date(tenant.suspendedAt).toLocaleString() : '—'} />
                <Row label="Cancelled" value={tenant.cancelledAt ? new Date(tenant.cancelledAt).toLocaleString() : '—'} />
              </Section>

              <Section title="Subscription">
                {!subscription ? <div style={{ fontSize: 11, color: '#7A8899' }}>No subscription (manual tenant).</div> : (
                  <>
                    <Row label="Marketplace id" value={<code style={{ fontSize: 10 }}>{subscription.marketplaceSubscriptionId}</code>} />
                    <Row label="Offer" value={subscription.offerId} />
                    <Row label="Plan" value={subscription.planId} />
                    <Row label="Quantity" value={subscription.quantity} />
                    <Row label="State" value={subscription.state} />
                    <Row label="Free trial" value={subscription.isFreeTrial ? 'Yes' : 'No'} />
                    <Row label="Term" value={subscription.term || '—'} />
                    <Row label="Purchaser" value={subscription.purchaserEmail} />
                    <Row label="Beneficiary" value={subscription.beneficiaryEmail || '—'} />
                    <Row label="Last synced" value={new Date(subscription.lastSyncedAt).toLocaleString()} />
                  </>
                )}
              </Section>

              <Section title={`Webhooks (${webhooks.length})`}>
                {webhooks.length === 0 ? <div style={{ fontSize: 11, color: '#7A8899' }}>No webhook events recorded.</div> : (
                  <div style={{ maxHeight: 240, overflowY: 'auto', border: '1px solid #F0F2F5', borderRadius: 6 }}>
                    {webhooks.map(w => (
                      <div key={w.id} style={{
                        padding: '8px 10px', borderBottom: '1px solid #F0F2F5',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      }}>
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 600, color: '#0D1B2A' }}>{w.action}</div>
                          <div style={{ fontSize: 9, color: '#7A8899' }}>
                            {new Date(w.receivedAt).toLocaleString()} ·
                            <span style={{
                              marginLeft: 6, padding: '1px 6px', borderRadius: 4, fontSize: 9,
                              background: w.status === 'Processed' ? '#E8F5E9' :
                                         w.status === 'Failed' ? '#FFEBEE' : '#F5F7F9',
                              color: w.status === 'Processed' ? '#2E7D32' :
                                     w.status === 'Failed' ? '#C62828' : '#455A64',
                            }}>{w.status}</span>
                          </div>
                          {w.failureReason && (
                            <div style={{ fontSize: 9, color: '#C62828', marginTop: 2 }}>{w.failureReason}</div>
                          )}
                        </div>
                        {w.status === 'Failed' && (
                          <button className="btn-secondary" style={{ fontSize: 9 }} disabled={busy}
                            onClick={() => replay(w.id)}>Replay</button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </Section>

              <Section title="Actions">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {!tenant.isManual && (
                    <button className="btn-secondary" disabled={busy} onClick={resync}>Resync from Marketplace</button>
                  )}
                  {!showMarkManual ? (
                    <button className="btn-secondary" disabled={busy} onClick={() => setShowMarkManual(true)}>Mark as manual</button>
                  ) : (
                    <div style={{ flex: 1, padding: 10, background: '#F8F9FA', borderRadius: 6 }}>
                      <div style={{ fontSize: 10, color: '#7A8899', marginBottom: 6 }}>Display name</div>
                      <input value={manualDisplayName} onChange={e => setManualDisplayName(e.target.value)}
                        style={{ width: '100%', padding: '6px 8px', fontSize: 11, border: '1px solid #E0E4E8', borderRadius: 4, marginBottom: 8 }} />
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn-primary" style={{ fontSize: 10 }} disabled={busy} onClick={markManual}>Confirm</button>
                        <button className="btn-secondary" style={{ fontSize: 10 }} onClick={() => setShowMarkManual(false)}>Cancel</button>
                      </div>
                    </div>
                  )}
                  {!showRebind ? (
                    <button className="btn-secondary" disabled={busy} onClick={() => setShowRebind(true)}>Rebind purchaser email</button>
                  ) : (
                    <div style={{ flex: 1, padding: 10, background: '#F8F9FA', borderRadius: 6 }}>
                      <div style={{ fontSize: 10, color: '#7A8899', marginBottom: 6 }}>New purchaser email</div>
                      <input type="email" value={rebindEmail} onChange={e => setRebindEmail(e.target.value)}
                        placeholder="new@example.com"
                        style={{ width: '100%', padding: '6px 8px', fontSize: 11, border: '1px solid #E0E4E8', borderRadius: 4, marginBottom: 8 }} />
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn-primary" style={{ fontSize: 10 }} disabled={busy} onClick={rebind}>Confirm</button>
                        <button className="btn-secondary" style={{ fontSize: 10 }} onClick={() => setShowRebind(false)}>Cancel</button>
                      </div>
                    </div>
                  )}
                </div>
              </Section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const th = { padding: '8px 12px', textAlign: 'left', color: '#455A64', fontWeight: 600 };
const td = { padding: '8px 12px', color: '#0D1B2A' };

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#0D1B2A', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div style={{ display: 'flex', padding: '4px 0', fontSize: 11, borderBottom: '1px solid #F8F9FA' }}>
      <div style={{ width: 130, color: '#7A8899' }}>{label}</div>
      <div style={{ flex: 1, color: '#0D1B2A', fontWeight: 500, wordBreak: 'break-word' }}>{value}</div>
    </div>
  );
}
